// @flow

import * as React from 'react';

import { Avatar } from '@ta/styleguide.avatar';
import { MemberName, MemberStatsBasic, MemberReasonWhy } from '@ta/social.member-common';
import FollowButton from '@ta/social.follow';
import { Link } from '@ta/common.routing';

import type { MemberInfo } from '@ta/social.member-types';
import type { Profile } from '../types';

import styles from './ProfileCardWide.less';

type Props = {
  profile: Profile,
  memberInfo?: MemberInfo,
  /** Optional function to execute after the follow button is pressed */
  onFollow?: (interactionType: 'FOLLOW' | 'UNFOLLOW') => void,
  /** Wrap the entire card in an anchor tag to the users profile, defaults true */
  addProfileLink?: boolean,
  /** Show the facebook icon next to the user's name, defaults to false */
  showFacebookIcon?: boolean,
};

const ProfileCardWide = ({
  profile,
  memberInfo,
  onFollow,
  addProfileLink,
  showFacebookIcon,
}: Props) => {
  const ProfileCard = (
    <div className={`ui_card section ${styles.card}`}>
      <div className={styles.avatar}>
        <Avatar photo={profile.avatar} size="large" withBorder />
      </div>

      <div className={styles.info}>
        <div className={styles.member_name_container}>
          <MemberName
            profile={profile}
            memberInfo={memberInfo}
            addProfileLink={false}
            socialConnection={showFacebookIcon ? 'FACEBOOK' : ''}
          />
        </div>
        <div className={styles.member_stats_container}>
          <MemberStatsBasic profile={profile} />
        </div>
        {profile.bio && (
          <div className={`${styles.bio_container} ${styles.bio}`}>{profile.bio}</div>
        )}
        {memberInfo && memberInfo.infoWithMember && (
          <MemberReasonWhy
            type={memberInfo.suggestedFolloweeType}
            reason={memberInfo.infoWithMember.reason}
            className={styles.reason_why}
            singleLine
          />
        )}
      </div>

      {/* Using an <EventBoundary> doesn't work here because we also need to preventDefault() otherwise the <a> tag that is in the parent still would do it's job */}
      {!profile.isMe && (
        <div className={styles.follow}>
          <FollowButton
            profile={profile}
            size="small"
            displayType="button+icon"
            preventClickPropagation
            onToggleFollow={onFollow}
          />
        </div>
      )}
    </div>
  );

  return addProfileLink ? <Link href={profile?.route?.url}>{ProfileCard}</Link> : ProfileCard;
};

ProfileCardWide.defaultProps = {
  memberInfo: undefined,
  onFollow: undefined,
  addProfileLink: true,
  showFacebookIcon: false,
};

export default ProfileCardWide;
